/**
 * Coarse "is this a low-memory device?" probe, used to dial back the map's
 * render budget.
 *
 * The WebGL drawing buffer is the single largest allocation the app makes, and
 * it scales with the *square* of the device pixel ratio: a 400x800 CSS viewport
 * at DPR 3 is a 1200x2400 buffer (~11 MB), and MapLibre holds more than one.
 * Old handsets pair a high DPR with very little GPU memory, which is exactly the
 * combination that gets the tab killed. Rendering those at a clamped ratio costs
 * some sharpness and buys a ~4x reduction in framebuffer memory and fill rate.
 *
 * Both signals are advisory. `deviceMemory` is Chromium-only and is bucketed
 * (0.25/0.5/1/2/4/8), and `hardwareConcurrency` is missing on old Safari — hence
 * the `?? ` fallbacks that treat "unknown" as "not low end", so a modern browser
 * that simply withholds the hint is never penalised.
 */

interface NavigatorWithHints extends Navigator {
  deviceMemory?: number;
}

/** Upper bound on the render resolution of low-end devices. */
const LOW_END_MAX_PIXEL_RATIO = 1.5;

/** MapLibre's own tile cache, in tiles. Its default scales with viewport size. */
const LOW_END_MAX_TILE_CACHE = 20;

let cachedIsLowEnd: boolean | null = null;

/**
 * True when the device reports <= 2 GB of RAM or <= 4 logical cores.
 * Evaluated once — neither hint changes over a session.
 */
export function isLowEndDevice(): boolean {
  if (cachedIsLowEnd !== null) {
    return cachedIsLowEnd;
  }

  if (typeof navigator === 'undefined') {
    cachedIsLowEnd = false;
    return cachedIsLowEnd;
  }

  const memoryGb = (navigator as NavigatorWithHints).deviceMemory ?? Infinity;
  const cores = navigator.hardwareConcurrency ?? Infinity;

  cachedIsLowEnd = memoryGb <= 2 || cores <= 4;
  return cachedIsLowEnd;
}

/**
 * Device pixel ratio to hand MapLibre. Full ratio on capable hardware, clamped
 * on low-end devices where the framebuffer is the thing most likely to OOM.
 */
export function getMapPixelRatio(): number {
  const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  return isLowEndDevice() ? Math.min(dpr, LOW_END_MAX_PIXEL_RATIO) : dpr;
}

/**
 * Tile-cache cap for MapLibre. `null` leaves its viewport-derived default in
 * place; only low-end devices get an explicit ceiling.
 */
export function getMapMaxTileCacheSize(): number | null {
  return isLowEndDevice() ? LOW_END_MAX_TILE_CACHE : null;
}
